/**
 * Governance / Audit view (Doc 17 §13).
 * Authorization events, supersession, overrides, cycle closure.
 * Replan: Propose replan (create new version from current), Authorize POR.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, Shield, CheckCircle, FileStack, RotateCcw, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { NoPlanCycleState } from '../../components/planning/NoPlanCycleState';
import { PlanningListSkeleton } from '../../components/planning/PlanningListSkeleton';
import { PorModal, PrimaryButton, GhostButton } from '../../components/plan-of-record/porUi';
import {
  getFirstActiveCycleProvisional,
  getPlanningAuditEvents,
  listPlanVersions,
  getActivePlanOfRecord,
  createPlanVersionFromCurrent,
  getDecisionTitlesByIds,
  authorizeNewPlanOfRecord,
  PLANNING_OVERRIDE_EVENT_TYPES,
  listReprioritizationTriggersForPlanCycle,
  acknowledgeReprioritizationTrigger,
  closeReprioritizationTrigger,
  getPlanVersionLineContents,
  listPlanOfRecordHistory,
  previewPorAuthorization,
  listDivergencesForPlanVersion,
} from '../../lib/planningService';
import type {
  PlanningAuditEvent,
  ReprioritizationTriggerRow,
  PlanVersionLineEntry,
} from '../../lib/planningService';
import type { PorBlocker } from '../../lib/planningQueries';
import { SEVERE_OVERRIDE_RULE } from '../../lib/planningQueries';
import { versionRowState, versionRowCaption, type PlanOfRecordRow } from '../../lib/planning/versionRowState';
import type { PlanVersion, RankSequenceDivergence } from '../../types/planning';
import { usePlanningGovernanceAllowed } from '../../hooks/usePlanningGovernanceAllowed';
import { DivergencePanel } from '../../components/planning/DivergencePanel';
import { usePlanAuthorizationRight } from '../../hooks/prioritization/usePlanAuthorizationRight';
import { PlanAuditPanel } from '../../components/planning/PlanAuditPanel';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import {
  AUTHORIZE_POR_LABEL,
  AUTHORIZING_POR_LABEL,
  CANNOT_AUTHORIZE_YET_LABEL,
  NEVER_AUTHORIZED_LABEL,
  NO_POR_TO_CHANGE_REASON,
  PROPOSE_REPLAN_LABEL,
} from '../../lib/planning/vocabulary';
import { useItemHref } from '@/lib/itemLinkScope';

const CHANGE_REASONS: { value: PlanVersion['change_reason']; label: string }[] = [
  { value: 'constraint_shift', label: 'Constraint shift' },
  { value: 'assumption_break', label: 'Assumption break' },
  { value: 'scope_adjustment', label: 'Scope adjustment' },
  { value: 'other', label: 'Other' },
];

/** Why a priority did not make the cut, in the engine's own words. */
function cutReason(entry: PlanVersionLineEntry): string {
  const decision = entry.decision.replace(/^Out - /, 'No room — ').replace(/^Deferred - /, 'No room — ').replace(/^Blocked - /, 'Blocked — ');
  return entry.reason ? `${decision}: ${entry.reason}` : decision;
}

/**
 * What a version holds, split at the line.
 *
 * Above the line is the commitment. Below the line is what was considered in
 * the same pass and did not make it — kept on the record so the plan says what
 * was traded away, not only what was kept.
 */
function VersionContents({
  state,
}: {
  state: { above: PlanVersionLineEntry[]; below: PlanVersionLineEntry[]; error: string | null } | 'loading' | undefined;
}) {
  if (state === undefined) return null;
  if (state === 'loading') {
    return <p className="mt-3 pl-1 text-sm text-[var(--text-ter)]">Reading the plan&hellip;</p>;
  }
  if (state.error) {
    return (
      <p className="mt-3 pl-1 text-sm text-[var(--red)]" role="alert">
        {state.error}
      </p>
    );
  }
  if (state.above.length === 0 && state.below.length === 0) {
    return (
      <p className="mt-3 pl-1 text-sm text-[var(--text-ter)]">
        This version has no priorities on it, so there is nothing to authorize.
      </p>
    );
  }

  return (
    <div className="mt-3 grid gap-4 sm:grid-cols-2">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">
          In the plan <span className="font-normal text-[var(--text-ter)]">({state.above.length})</span>
        </h3>
        <ol className="mt-1 space-y-1">
          {state.above.map((e) => (
            <li key={e.priorityId} className="text-sm text-[var(--text-sec)] flex gap-2">
              <span className="text-[var(--text-ter)] tabular-nums w-5 text-right shrink-0">{e.position ?? '—'}</span>
              <span>{e.name}</span>
            </li>
          ))}
        </ol>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">
          Below the line <span className="font-normal text-[var(--text-ter)]">({state.below.length})</span>
        </h3>
        {state.below.length === 0 ? (
          <p className="mt-1 text-sm text-[var(--text-ter)]">Nothing was cut — everything considered is in the plan.</p>
        ) : (
          <ol className="mt-1 space-y-1">
            {state.below.map((e) => (
              <li key={e.priorityId} className="text-sm text-[var(--text-sec)] flex gap-2">
                <span className="text-[var(--text-ter)] tabular-nums w-5 text-right shrink-0">{e.position ?? '—'}</span>
                <span>
                  {e.name}
                  <span className="block text-xs text-[var(--text-ter)]">{cutReason(e)}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function loadData(
  cycleId: string,
  setEvents: (e: PlanningAuditEvent[]) => void,
  setVersions: (v: PlanVersion[]) => void,
  setActivePorVersionId: (id: string | null) => void,
  setTriggers: (t: ReprioritizationTriggerRow[]) => void,
  setLoading: (l: boolean) => void,
  setPorHistory: (rows: PlanOfRecordRow[]) => void = () => {},
) {
  setLoading(true);
  Promise.all([
    getPlanningAuditEvents(cycleId),
    listPlanVersions(cycleId),
    getActivePlanOfRecord(cycleId),
    listReprioritizationTriggersForPlanCycle(cycleId),
    listPlanOfRecordHistory(cycleId),
  ]).then(([evRes, verRes, porRes, trigRes, histRes]) => {
    setEvents(evRes.data ?? []);
    setVersions(verRes.data ?? []);
    setActivePorVersionId(porRes.data?.plan_version_id ?? null);
    setTriggers(trigRes.data ?? []);
    setPorHistory(histRes.data ?? []);
    setLoading(false);
  });
}

export default function PlanningGovernance() {
  const itemHref = useItemHref();
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const cycleIdParam = cycleIdFromParams ?? searchParams.get('cycleId');
  // A change-course answer carried its decision here: the replan it proposes
  // records that decision as the new version's why (canon 23).
  const changeCourseDecisionId = searchParams.get('changeCourse');
  const [cycleId, setCycleId] = useState<string | null>(cycleIdParam);
  const [events, setEvents] = useState<PlanningAuditEvent[]>([]);
  const [versions, setVersions] = useState<PlanVersion[]>([]);
  const [activePorVersionId, setActivePorVersionId] = useState<string | null>(null);
  const [porHistory, setPorHistory] = useState<PlanOfRecordRow[]>([]);
  // What authorizing the chosen version would run into — read before the
  // dialog shows its button, so the refusal names the item, not the law.
  const [authorizePreview, setAuthorizePreview] = useState<{ ready: boolean; blockers: PorBlocker[] } | null>(null);
  const [authorizePreviewLoading, setAuthorizePreviewLoading] = useState(false);
  const [authorizeDivergences, setAuthorizeDivergences] = useState<RankSequenceDivergence[]>([]);
  const [triggers, setTriggers] = useState<ReprioritizationTriggerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNonActiveCycle, setIsNonActiveCycle] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [replanReason, setReplanReason] = useState<PlanVersion['change_reason']>('other');
  const [decisionTitles, setDecisionTitles] = useState<Record<string, string>>({});
  const [replanning, setReplanning] = useState(false);
  const [authorizing, setAuthorizing] = useState<string | null>(null);
  const [triggerActionId, setTriggerActionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Authorizing a POR is a governance transition, not a one-click toggle — confirm first.
  const [confirmAuthorize, setConfirmAuthorize] = useState<PlanVersion | null>(null);
  const [openContents, setOpenContents] = useState<string | null>(null);
  const [contents, setContents] = useState<
    Record<string, { above: PlanVersionLineEntry[]; below: PlanVersionLineEntry[]; error: string | null } | 'loading'>
  >({});
  const [authorizedVersion, setAuthorizedVersion] = useState<PlanVersion | null>(null);
  const [proposedVersion, setProposedVersion] = useState<PlanVersion | null>(null);

  const canGovern = usePlanningGovernanceAllowed();
  const governanceDeniedReason = canGovern
    ? null
    : 'Only workspace admins and owners can authorize a Plan of Record or propose a replan.';

  // Decision rights (D-4 phase 2, mig 843): when the cycle this plan was built
  // from has an "Items / selection" right, only its holder authorizes. The
  // Authorize button is not drawn for anyone else; they see who holds it.
  const planRight = usePlanAuthorizationRight(cycleId);
  const authorizeRightLine = planRight.refusalLine;
  const canAuthorize = canGovern && planRight.allowed;

  const refresh = useCallback(() => {
    if (!cycleId) return;
    loadData(cycleId, setEvents, setVersions, setActivePorVersionId, setTriggers, setLoading, setPorHistory);
  }, [cycleId]);

  useEffect(() => {
    const overrideCycleId = cycleIdParam ?? selectedCycleId;
    if (overrideCycleId) {
      setCycleId(overrideCycleId);
      loadData(overrideCycleId, setEvents, setVersions, setActivePorVersionId, setTriggers, setLoading, setPorHistory);
      return;
    }
    getFirstActiveCycleProvisional().then(({ data }) => {
      if (data) {
        setCycleId(data.cycle.id);
        setIsNonActiveCycle(data.isNonActiveCycle ?? false);
        loadData(data.cycle.id, setEvents, setVersions, setActivePorVersionId, setTriggers, setLoading, setPorHistory);
      } else {
        setLoading(false);
      }
    });
  }, [cycleIdParam, selectedCycleId]);

  // Resolve decision titles for the "because" lines: the carried change-course
  // decision plus any versions that already record a source decision.
  useEffect(() => {
    const ids = [
      ...new Set(
        [
          changeCourseDecisionId,
          ...versions.map((v) => v.source_decision_id ?? null),
        ].filter((id): id is string => !!id)
      ),
    ];
    if (ids.length === 0) return;
    void getDecisionTitlesByIds(ids).then(({ data }) => {
      setDecisionTitles((prev) => ({ ...prev, ...data }));
    });
  }, [changeCourseDecisionId, versions]);

  /** Show what a version actually holds — the commitment, and what it cut. */
  const toggleContents = async (versionId: string) => {
    if (openContents === versionId) {
      setOpenContents(null);
      return;
    }
    setOpenContents(versionId);
    if (contents[versionId] && contents[versionId] !== 'loading') return;
    setContents((prev) => ({ ...prev, [versionId]: 'loading' }));
    const res = await getPlanVersionLineContents(versionId);
    setContents((prev) => ({ ...prev, [versionId]: res }));
  };

  const onProposeReplan = async () => {
    if (!cycleId || !activePorVersionId) return;
    setError(null);
    setProposedVersion(null);
    setReplanning(true);
    const { data, error: err } = await createPlanVersionFromCurrent(cycleId, replanReason, {
      sourceDecisionId: changeCourseDecisionId,
    });
    setReplanning(false);
    if (err) {
      setError(err);
      return;
    }
    if (data) {
      setProposedVersion(data);
      // The carried decision is now recorded on the version — clear the param
      // so a second replan doesn't silently reuse it.
      if (changeCourseDecisionId) {
        setSearchParams((prev) => {
          const params = new URLSearchParams(prev);
          params.delete('changeCourse');
          return params;
        }, { replace: true });
      }
      refresh();
    }
  };

  // Returns true when the version was authorized. The caller keeps the confirm
  // dialog open on false so the refusal lands where the click did: the page-level
  // banner sits at the very top, far above the Authorize button on a long
  // Governance page, so a refusal shown only there reads as "nothing happened".
  const openAuthorizeDialog = (version: PlanVersion) => {
    setError(null);
    setConfirmAuthorize(version);
    setAuthorizePreview(null);
    if (!cycleId) return;
    setAuthorizePreviewLoading(true);
    void loadAuthorizePreview(version);
  };

  const loadAuthorizePreview = async (version: PlanVersion) => {
    if (!cycleId) return;
    const { data } = await previewPorAuthorization(cycleId, version.id);
    const divergences = await listDivergencesForPlanVersion(version.id);
    setAuthorizePreview(data);
    setAuthorizeDivergences(divergences.data ?? []);
    setAuthorizePreviewLoading(false);
  };

  const onAuthorizePor = async (version: PlanVersion): Promise<boolean> => {
    if (!cycleId) return false;
    setError(null);
    setAuthorizedVersion(null);
    setProposedVersion(null);
    setAuthorizing(version.id);
    const { error: err } = await authorizeNewPlanOfRecord(cycleId, version.id);
    setAuthorizing(null);
    if (err) {
      setError(err);
      return false;
    }
    setAuthorizedVersion(version);
    refresh();
    return true;
  };

  const onAcknowledgeTrigger = async (triggerId: string) => {
    setError(null);
    setTriggerActionId(triggerId);
    const { error: err } = await acknowledgeReprioritizationTrigger(triggerId);
    setTriggerActionId(null);
    if (err) setError(err);
    else refresh();
  };

  const onCloseTrigger = async (triggerId: string) => {
    setError(null);
    setTriggerActionId(triggerId);
    const { error: err } = await closeReprioritizationTrigger(triggerId);
    setTriggerActionId(null);
    if (err) setError(err);
    else refresh();
  };

  return (
    <div className="max-w-5xl">
      <PageHeader
        icon={<Shield className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50 dark:bg-[var(--surface)]"
        title="Governance / Audit"
        subtitle="Authorization events, supersession, overrides, cycle closure."
      />

      {error && (
        <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
          {error}
        </div>
      )}

      {proposedVersion && !authorizedVersion && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          Version {proposedVersion.version_number} proposed from the plan of record. Review it below, then{' '}
          {AUTHORIZE_POR_LABEL.toLowerCase()} when it is ready.
        </div>
      )}

      {authorizedVersion && (
        <div className="flex items-center justify-between gap-3 mb-4 p-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            Version {authorizedVersion.version_number} is now the Plan of Record.
          </span>
          {cycleId && (
            <Link
              to={`/planning/plans/${cycleId}`}
              className="inline-flex items-center gap-1 font-medium underline whitespace-nowrap"
            >
              Next: Activate plan <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      )}

      {loading ? (
        <PlanningListSkeleton className="min-h-[320px]" />
      ) : !cycleId ? (
        <NoPlanCycleState onSelectCycle={(id) => { setSelectedCycleId(id); setLoading(true); }} />
      ) : (
        <>
          {isNonActiveCycle && (
            <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Viewing a non-active plan cycle. Activate it from{' '}
              <Link to="/planning/plans" className="font-medium underline">Planning Home</Link> when ready.
            </div>
          )}
          {/* A change-course answer carried its decision here — the proposed
              replan records it as the new version's why. */}
          {changeCourseDecisionId && (
            <div className="flex items-start gap-2 px-4 py-3 mb-4 bg-primary-50 border border-primary-200 text-sm text-primary-900">
              <RotateCcw className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium">Carrying out a change of course:</span>{' '}
                <Link
                  to={`/prioritization/decisions/${changeCourseDecisionId}`}
                  className="underline hover:no-underline"
                >
                  {decisionTitles[changeCourseDecisionId] ?? 'the decision on the record'}
                </Link>
                <p className="text-xs text-primary-700 mt-0.5">
                  Propose the replan below — the new plan version records this decision as its
                  why, and the change log links them.
                </p>
              </div>
            </div>
          )}

          {/* Replan + Authorize section */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)] flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-semibold text-[var(--text-pri)]">Plan versions &amp; replan</h2>
              {canGovern ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={replanReason ?? 'other'}
                    onChange={(e) => setReplanReason((e.target.value as PlanVersion['change_reason']) ?? 'other')}
                    className="border border-[var(--rule)] rounded px-2 py-1.5 text-sm"
                  >
                    {CHANGE_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={onProposeReplan}
                    disabled={replanning || !activePorVersionId}
                    title={!activePorVersionId ? NO_POR_TO_CHANGE_REASON : undefined}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    <FileStack className="h-4 w-4" />
                    {replanning ? 'Creating…' : PROPOSE_REPLAN_LABEL}
                  </button>
                  {!activePorVersionId && (
                    <span className="text-xs text-[var(--text-ter)] basis-full">{NO_POR_TO_CHANGE_REASON}</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-[var(--text-ter)]">{governanceDeniedReason}</span>
              )}
            </div>
            <ul className="divide-y divide-[var(--rule)]">
              {versions.map((v) => (
                <li key={v.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="font-medium text-[var(--text-pri)]">Version {v.version_number}</span>
                    {v.change_reason && (
                      <span className="ml-2 text-[var(--text-ter)] text-sm">({v.change_reason.replace(/_/g, ' ')})</span>
                    )}
                    {v.source_decision_id && (
                      <span className="ml-2 text-[var(--text-ter)] text-sm">
                        because{' '}
                        <Link
                          to={`/prioritization/decisions/${v.source_decision_id}`}
                          className="text-primary-600 hover:underline"
                        >
                          {decisionTitles[v.source_decision_id] ?? 'a decision on the record'}
                        </Link>
                      </span>
                    )}
                    <p className="text-sm text-[var(--text-ter)]">
                      {new Date(v.created_at).toLocaleString()}
                      {v.created_by && ` · ${v.created_by}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => toggleContents(v.id)}
                      aria-expanded={openContents === v.id}
                      className="inline-flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                    >
                      {openContents === v.id
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />}
                      What&rsquo;s in it
                    </button>
                    {(() => {
                      // One row, one meaning: the plan of record, the version to
                      // authorize next, a version that held it once, or one that
                      // never did. Only the candidate offers the act (§2.8).
                      const state = versionRowState(v, activePorVersionId, porHistory, versions);
                      if (state.kind === 'current') {
                        return (
                          <span className="inline-flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)] text-sm">
                            <CheckCircle className="h-4 w-4" /> Plan of record
                          </span>
                        );
                      }
                      if (state.kind === 'candidate') {
                        if (canGovern && !canAuthorize) {
                          return (
                            <span className="text-xs text-[var(--text-ter)] max-w-xs text-right">
                              {authorizeRightLine}
                            </span>
                          );
                        }
                        return canAuthorize ? (
                          <button
                            type="button"
                            onClick={() => openAuthorizeDialog(v)}
                            disabled={authorizing === v.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--green)] text-white rounded text-sm hover:bg-[var(--green)] disabled:opacity-50"
                          >
                            {authorizing === v.id ? AUTHORIZING_POR_LABEL : AUTHORIZE_POR_LABEL}
                          </button>
                        ) : (
                          <span className="text-xs text-[var(--text-ter)]" title={governanceDeniedReason ?? undefined}>
                            {governanceDeniedReason}
                          </span>
                        );
                      }
                      const caption = versionRowCaption(state, (iso) => new Date(iso).toLocaleDateString());
                      return (
                        <span className="text-sm text-[var(--text-ter)]">
                          {caption ?? NEVER_AUTHORIZED_LABEL}
                        </span>
                      );
                    })()}
                  </div>
                  </div>
                  {openContents === v.id && <VersionContents state={contents[v.id]} />}
                </li>
              ))}
            </ul>
            {versions.length === 0 && (
              <div className="px-4 py-6 text-[var(--text-ter)] text-sm">No plan versions yet. Create a plan from Planning Home, or propose a replan above.</div>
            )}
          </div>

          {/* Plan audit (PRD-plan-audit): the version offered for authorization
              next, or the plan of record when there is none. Informs only. */}
          {(() => {
            const candidates = versions.filter(
              (v) => versionRowState(v, activePorVersionId, porHistory, versions).kind === 'candidate',
            );
            const audited =
              candidates[candidates.length - 1] ?? versions.find((v) => v.id === activePorVersionId) ?? null;
            return (
              <PlanAuditPanel
                planVersionId={audited?.id ?? null}
                versionNumber={audited?.version_number ?? null}
                workspaceId={getCurrentWorkspaceId()}
              />
            );
          })()}

          {/* Reprioritization triggers (Doc 15 §5, 17) */}
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--slate-dim)]">
              <h2 className="font-semibold text-[var(--text-pri)]">Reprioritization triggers</h2>
              <p className="text-sm text-[var(--text-sec)] mt-0.5">Recommendation / open / acknowledge / close (Doc 15 §5.1)</p>
            </div>
            {triggers.length === 0 ? (
              <div className="px-4 py-6 text-[var(--text-sec)] text-sm">No reprioritization triggers for this cycle.</div>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {triggers.map((t) => (
                  <li key={t.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-[var(--text-pri)]">
                        <span className="inline-flex items-center gap-1">
                          <RotateCcw className="h-4 w-4 text-[var(--text-ter)]" />
                          {t.status}
                        </span>
                        <span className="ml-2 text-[var(--text-ter)] text-sm">
                          {new Date(t.triggered_at ?? t.created_at).toLocaleString()}
                        </span>
                      </p>
                      {t.reason && typeof t.reason === 'object' && Object.keys(t.reason).length > 0 && (
                        <p className="text-sm text-[var(--text-sec)] mt-0.5">
                          {typeof (t.reason as { reason?: string }).reason === 'string'
                            ? (t.reason as { reason: string }).reason
                            : JSON.stringify(t.reason).slice(0, 120)}
                          {JSON.stringify(t.reason).length > 120 ? '…' : ''}
                        </p>
                      )}
                    </div>
                    {t.status === 'open' && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onAcknowledgeTrigger(t.id)}
                          disabled={triggerActionId === t.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--mustard)] text-white rounded text-sm hover:bg-[var(--mustard)] disabled:opacity-50"
                        >
                          {triggerActionId === t.id ? '…' : 'Acknowledge'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onCloseTrigger(t.id)}
                          disabled={triggerActionId === t.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-[var(--slate)] text-white rounded text-sm hover:bg-[var(--bg-raised)] disabled:opacity-50"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Overrides (who/what/why) — Doc 17 §13 */}
          {(() => {
            const overrideEvents = events.filter((ev) => PLANNING_OVERRIDE_EVENT_TYPES.includes(ev.event_type as typeof PLANNING_OVERRIDE_EVENT_TYPES[number]));
            return (
              <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden mb-6">
                <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--mustard-dim)]">
                  <h2 className="font-semibold text-[var(--text-pri)]">Overrides</h2>
                  <p className="text-sm text-[var(--text-sec)] mt-0.5">Who / what / why — recorded deviations (Doc 14 §4)</p>
                </div>
                {overrideEvents.length === 0 ? (
                  <div className="px-4 py-6 text-[var(--text-sec)] text-sm">No overrides for this cycle.</div>
                ) : (
                  <ul className="divide-y divide-[var(--rule)]">
                    {overrideEvents.map((ev) => (
                      <li key={ev.id} className="px-4 py-3">
                        <p className="font-medium text-[var(--text-pri)]">{ev.event_type.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-[var(--text-sec)]">
                          {new Date(ev.occurred_at).toLocaleString()}
                          {ev.actor_id && ` · Who: ${ev.actor_id}`}
                        </p>
                        {ev.reason && <p className="text-sm text-[var(--text-sec)] mt-1">Why: {ev.reason}</p>}
                        {ev.payload?.impacted_object_ids && Array.isArray(ev.payload.impacted_object_ids) && (ev.payload.impacted_object_ids as string[]).length > 0 && (
                          <p className="text-xs text-[var(--text-ter)] mt-1">What: {((ev.payload.impacted_object_ids as string[]).slice(0, 5).join(', '))}{(ev.payload.impacted_object_ids as string[]).length > 5 ? '…' : ''}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })()}

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
              <h2 className="font-semibold text-[var(--text-pri)]">Audit events</h2>
            </div>
            {events.length === 0 ? (
              <div className="px-4 py-6 text-[var(--text-sec)] text-sm">No audit events for this cycle yet.</div>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {events.map((ev) => (
                  <li key={ev.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-[var(--text-pri)]">{ev.event_type.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-[var(--text-sec)]">
                          {new Date(ev.occurred_at).toLocaleString()}
                          {ev.actor_id && ` · ${ev.actor_id}`}
                        </p>
                        {ev.reason && <p className="text-sm text-[var(--text-ter)] mt-1">{ev.reason}</p>}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="px-4 py-3 border-t border-[var(--rule)] bg-[var(--surface)]">
              <Link to={`/planning/plans/${cycleId}`} className="text-sm text-primary-600 hover:underline">
                View plan →
              </Link>
            </div>
          </div>
        </>
      )}

      {confirmAuthorize && (
        <PorModal
          title="Authorize as Plan of Record?"
          subtitle={`Version ${confirmAuthorize.version_number}`}
          onClose={() => setConfirmAuthorize(null)}
          footer={
            <>
              <GhostButton onClick={() => setConfirmAuthorize(null)}>Cancel</GhostButton>
              <PrimaryButton
                disabled={
                  authorizing === confirmAuthorize.id ||
                  authorizePreviewLoading ||
                  (authorizePreview != null && !authorizePreview.ready)
                }
                onClick={() => {
                  const version = confirmAuthorize;
                  void onAuthorizePor(version).then((ok) => {
                    if (ok) setConfirmAuthorize(null);
                  });
                }}
              >
                {authorizing === confirmAuthorize.id ? AUTHORIZING_POR_LABEL : AUTHORIZE_POR_LABEL}
              </PrimaryButton>
            </>
          }
        >
          {authorizePreviewLoading && (
            <p className="text-sm text-[var(--text-ter)]">Checking what this version needs…</p>
          )}
          {authorizePreview && !authorizePreview.ready && (
            <div role="alert" className="mb-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-sm">
              <p className="font-medium text-[var(--text-pri)]">{CANNOT_AUTHORIZE_YET_LABEL}</p>
              <ul className="mt-1.5 space-y-1.5 text-[var(--text-pri)]">
                {authorizePreview.blockers.map((b) =>
                  b.severe_checks && b.severe_checks.length > 0 ? (
                    b.severe_checks.map((check) => (
                      <li key={`${b.blocker_code}-${check.key}`}>
                        {check.targets.length > 0 ? (
                          <>
                            {check.targets.map((t, i) => (
                              <span key={t.workUnitId}>
                                {i > 0 && ', '}
                                {t.itemId ? (
                                  <Link to={itemHref(t.itemId)} className="font-medium underline">
                                    {t.title}
                                  </Link>
                                ) : (
                                  <span className="font-medium">{t.title}</span>
                                )}
                              </span>
                            ))}
                            {': '}
                          </>
                        ) : null}
                        {check.label}
                        {check.remediation ? ` — ${check.remediation.replace(/\.$/, '')}` : ''}. Then run the engine and commit again.
                      </li>
                    ))
                  ) : (
                    <li key={b.blocker_code}>
                      {b.title}
                      {b.remediation_route && (
                        <>
                          {' '}
                          <Link to={b.remediation_route} className="underline">
                            Fix it →
                          </Link>
                        </>
                      )}
                    </li>
                  ),
                )}
              </ul>
              {authorizePreview.blockers.some((b) => b.severe_checks && b.severe_checks.length > 0) && (
                <p className="mt-2 text-xs text-[var(--text-sec)]">{SEVERE_OVERRIDE_RULE}</p>
              )}
            </div>
          )}
          {authorizeDivergences.length > 0 && (
            <div className="mb-3">
              <DivergencePanel
                divergences={authorizeDivergences}
                canResolve={canAuthorize}
                onResolutionChange={() => void loadAuthorizePreview(confirmAuthorize)}
              />
            </div>
          )}
          <p className="text-sm text-[var(--text-sec)]">
            This makes version {confirmAuthorize.version_number} the Plan of Record for this cycle.
            Any version currently authorized as POR is superseded, and the linked narrative is frozen with
            provenance to this version. The authorization is written to the audit log below and cannot be
            reversed — a later change requires authorizing a new version instead.
          </p>
          {error && (
            <p
              role="alert"
              className="mt-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-sm text-[var(--coral)]"
            >
              {error}
            </p>
          )}
        </PorModal>
      )}
    </div>
  );
}
